/**
 * Finalizing a spoken (Live Conversation / Video) attempt when it ends.
 *
 * ── WHY THE CLIENT FINALIZES ───────────────────────────────────────────────────
 *
 * A spoken conversation is only scored when someone calls the canonical
 * finalize (POST /coaching/sessions/:id/finalize). Ending the conversation used
 * to call nothing: the user saw no result, and starting another attempt left the
 * spoken one unscored with no level outcome. The server's lifecycle sweeper
 * (CONVERSATION_LIFECYCLE_SWEEP_ENABLED, run with the conversation engine) is a
 * BACKSTOP for a tab that closes mid-conversation — not the way a result is
 * produced.
 *
 * ── EXACTLY ONCE PER ATTEMPT ───────────────────────────────────────────────────
 *
 * An attempt can end two ways that race — the user presses End, the server
 * completes it — and either may be reported more than once. The finalizer
 * requests finalize ONCE per session id. The server is idempotent too (one
 * result per attempt, one finalization at a time), but a second request would
 * still be a wasted, rate-limited call that could surface a confusing
 * FINALIZATION_IN_PROGRESS. Only an explicit user retry, after a retryable
 * refusal, asks again.
 *
 * ── REFUSALS ARE TOLD, NEVER SCORED ────────────────────────────────────────────
 *
 * Every refusal is worded from the one attempt-error map (utils/attemptErrors).
 * SESSION_ALREADY_FINALIZED is not a failure: the stored result is shown, with
 * the note that nothing was scored again.
 *
 * Plain TypeScript (no React) so the rule is testable without a DOM; the hook
 * below adapts it.
 */

import { useCallback, useRef, useState } from 'react';
import { ATTEMPT_ERROR_COPY, attemptErrorView, type AttemptErrorView } from '../utils/attemptErrors';
import type { FinalizeResult, LevelOutcome } from '../store/api/coachingApi';

export const SPOKEN_FINALIZE_FALLBACK =
  'Your conversation was saved, but the assessment could not be completed right now.';

export type SpokenFinalizeState =
  | { sessionId: string; phase: 'finalizing' }
  | {
      sessionId: string;
      phase: 'finalized';
      /** The NPC level outcome, for a level attempt. */
      level: LevelOutcome | null;
      /** Said alongside the result (e.g. it was already finalized), or null. */
      note: string | null;
    }
  | { sessionId: string; phase: 'refused'; error: AttemptErrorView };

export interface SpokenAttemptFinalizer {
  /** Finalize this attempt — once per session id. Resolves false when already requested. */
  finish(sessionId: string): Promise<boolean>;
  /** An explicit user retry after a retryable refusal. */
  retry(sessionId: string): Promise<void>;
  /** Whether finalize was already requested for this session. */
  requested(sessionId: string): boolean;
}

export function createSpokenAttemptFinalizer(deps: {
  finalize: (sessionId: string) => Promise<FinalizeResult>;
  onState: (state: SpokenFinalizeState) => void;
}): SpokenAttemptFinalizer {
  const requested = new Set<string>();

  const run = async (sessionId: string): Promise<void> => {
    deps.onState({ sessionId, phase: 'finalizing' });
    try {
      const result = await deps.finalize(sessionId);
      deps.onState({ sessionId, phase: 'finalized', level: result?.level ?? null, note: null });
    } catch (error) {
      const view = attemptErrorView(error, SPOKEN_FINALIZE_FALLBACK);
      if (view.code === 'SESSION_ALREADY_FINALIZED') {
        // The stored result stands; it is shown, and nothing was scored again.
        deps.onState({ sessionId, phase: 'finalized', level: null, note: view.message });
        return;
      }
      // An unknown failure (e.g. 503 evaluation failed) is worth another try;
      // a code the map knows says whether.
      const known = view.code !== null && ATTEMPT_ERROR_COPY[view.code] !== undefined;
      deps.onState({ sessionId, phase: 'refused', error: known ? view : { ...view, retryable: true } });
    }
  };

  return {
    async finish(sessionId) {
      if (!sessionId || requested.has(sessionId)) return false;
      requested.add(sessionId);
      await run(sessionId);
      return true;
    },
    async retry(sessionId) {
      requested.add(sessionId);
      await run(sessionId);
    },
    requested: (sessionId) => requested.has(sessionId),
  };
}

/**
 * React adapter: one finalizer for the component's life, reporting through the
 * LATEST `onState` (so a handler that reads props or state never goes stale).
 * Returns the finalizer and the last state it reported.
 */
export function useSpokenAttemptFinalizer(
  finalize: (sessionId: string) => Promise<FinalizeResult>,
  onState?: (state: SpokenFinalizeState) => void
): { finalizer: SpokenAttemptFinalizer; state: SpokenFinalizeState | null; clear: () => void } {
  const [state, setState] = useState<SpokenFinalizeState | null>(null);
  const finalizeRef = useRef(finalize);
  finalizeRef.current = finalize;
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const finalizerRef = useRef<SpokenAttemptFinalizer | null>(null);
  if (!finalizerRef.current) {
    finalizerRef.current = createSpokenAttemptFinalizer({
      finalize: (id) => finalizeRef.current(id),
      onState: (s) => {
        setState(s);
        onStateRef.current?.(s);
      },
    });
  }
  const clear = useCallback(() => setState(null), []);
  return { finalizer: finalizerRef.current, state, clear };
}
